// -- Node labels (Neo4j labels) --
//
// Each layer is defined as a `readonly` tuple first; the type alias is
// derived from `[number]`. This keeps a single source of truth so the
// runtime arrays exposed below (`NODE_LABELS` etc.) and the compile-time
// unions can never drift, which the cypher validator relies on.

export const PLC_NODE_LABELS = [
  'Controller',
  'Program',
  'Routine',
  'AOI',
  'PLCDataType',
  'PLCDataTypeMember',
  'PLCTag',
  'ControlPattern',
  'DataFlow'
] as const
export type PLCNodeLabel = (typeof PLC_NODE_LABELS)[number]

/**
 * Robotics-domain labels — Motoman/Yaskawa, FANUC, and Yamaha controller-backup
 * entities, including robot vision (FANUC iRVision) processes and vision data.
 *
 * Kept distinct from the PLC labels: a `RobotController` is not a PLC
 * `Controller`, and a `RobotJob` is neither a PLC `Program`/`Routine` nor a
 * `Procedure` (work-instruction/SOP). Robot cells themselves are modeled as
 * `Equipment` with `equipment_type: 'RoboticCell'`.
 */
export const ROBOTICS_NODE_LABELS = [
  'RobotController',
  'RobotArm',
  'RobotJob',
  'RobotIOSignal',
  'RobotCondition',
  'VisionProcess',
  'VisionData'
] as const
export type RoboticsNodeLabel = (typeof ROBOTICS_NODE_LABELS)[number]

/**
 * Machine-vision labels — Cognex In-Sight / DataMan / VisionPro backups.
 *
 * A `VisionSystem` is neither a PLC `Controller` nor a `RobotController`; its
 * PLC I/O stays on module connection `PLCTag`s linked via `HAS_IO_TAG`.
 * Robot-hosted vision (FANUC iRVision `VisionProcess` / `VisionData`) is part
 * of the robot controller and lives in {@link ROBOTICS_NODE_LABELS}.
 */
export const VISION_NODE_LABELS = ['VisionSystem', 'VisionJob'] as const
export type VisionNodeLabel = (typeof VISION_NODE_LABELS)[number]

/**
 * DCS-domain labels — Experion ERDB (and vendor-neutral DCS) entities.
 *
 * Kept distinct from PLC `Controller` / `PLCTag` and from SCADA `Tag` /
 * `TagProvider` / `Alarm`. Live values still come from Ignition later.
 */
export const DCS_NODE_LABELS = [
  'DcsController',
  'Cee',
  'ControlModule',
  'FunctionBlock',
  'BlockType',
  'BlockParamGroup',
  'BlockLibrary',
  'IoModule',
  'IoChannel',
  'ControlNamespace',
  'ControlTag',
  'ControlAlarm',
  'ControlSetpoint'
] as const
export type DcsNodeLabel = (typeof DCS_NODE_LABELS)[number]

/**
 * Org-domain labels — top-level tenant + locations.
 */
export const ORG_NODE_LABELS = ['Organization', 'Site'] as const
export type OrgNodeLabel = (typeof ORG_NODE_LABELS)[number]

/**
 * SOAP integration metadata — service endpoints, their WSDL contracts, and
 * contract-local operations. Runtime credentials remain outside the graph.
 */
export const SOAP_NODE_LABELS = ['SoapEndpoint', 'SoapContract', 'SoapOperation'] as const
export type SoapNodeLabel = (typeof SOAP_NODE_LABELS)[number]

/**
 * Compute-domain labels — physical hosts and the data sources they serve.
 *
 * `Compute` replaces the prior `Gateway` label.
 * `Tag` replaces the prior `ScadaTag` label.
 * `Database` replaces the prior `DatabaseConnection` label.
 */
export const COMPUTE_NODE_LABELS = [
  'Compute',
  'TagProvider',
  'Tag',
  'Folder',
  'Database',
  'Table',
  'Alarm'
] as const
export type ComputeNodeLabel = (typeof COMPUTE_NODE_LABELS)[number]

/**
 * Database-domain labels — Ignition-specific named queries and query tags.
 *
 * Note: `DatabaseConnection` was promoted into the compute domain as `Database`.
 * The remaining database labels are Ignition-internal and continue to live
 * under `nodespec/database.json`.
 */
export const DATABASE_NODE_LABELS = ['NamedQuery', 'QueryTag'] as const
export type DatabaseNodeLabel = (typeof DATABASE_NODE_LABELS)[number]

/**
 * Asset-domain labels — Equipment-adjacent ingest and AI-promoted concepts.
 */
export const ASSET_NODE_LABELS = ['EquipmentProperty', 'Observable', 'Setpoint', 'Part'] as const
export type AssetNodeLabel = (typeof ASSET_NODE_LABELS)[number]

/**
 * Ignition-internal labels retained verbatim from the previous SCADA layer.
 * Describe Ignition runtime structure at a different level of abstraction
 * from the rest of the ontology.
 */
export const IGNITION_INTERNAL_NODE_LABELS = [
  'Project',
  'View',
  'ViewComponent',
  'UDTDefinition',
  'UDTMember',
  'UDTParameter',
  'UDTInstance',
  'Script',
  'GatewayEvent',
  'GatewayEventHandler',
  'AuditProfile',
  'ScheduleProfile',
  'Holiday'
] as const
export type IgnitionInternalNodeLabel = (typeof IGNITION_INTERNAL_NODE_LABELS)[number]

/**
 * Process-domain labels.
 *
 * `Equipment` is a single label that covers vessels, pumps, valves, instruments,
 * motors, conveyors, etc. — disambiguated by the `equipment_type` property using
 * {@link KNOWN_EQUIPMENT_TYPES}. The legacy subtype labels (`Vessel`, `Pump`, …)
 * and `Instrument`, `ProcessLine`, `ControlLoop`, `Drive` no longer exist.
 */
export const EQUIPMENT_NODE_LABELS = ['Equipment'] as const
export type EquipmentNodeLabel = (typeof EQUIPMENT_NODE_LABELS)[number]

export const PROCESS_NODE_LABELS = [
  'Line',
  'Process',
  'LineOperation',
  ...EQUIPMENT_NODE_LABELS
] as const
export type ProcessNodeLabel = (typeof PROCESS_NODE_LABELS)[number]

/**
 * Recommended values for `Equipment.equipment_type`. The field is freeform: any string
 * is accepted, but ingest, UI dropdowns, and prompts default to this list. Use `'Other'`
 * for anything that does not fit, and consider widening this list before introducing
 * a new specialised type.
 */
export const KNOWN_EQUIPMENT_TYPES = [
  'Vessel',
  'HeatExchanger',
  'Pump',
  'Valve',
  'Motor',
  'Conveyor',
  'Actuator',
  'RoboticCell',
  'Workstation',
  'Instrument',
  'ControlLoop',
  'Drive',
  'Pipe',
  'Other'
] as const

export type KnownEquipmentType = (typeof KNOWN_EQUIPMENT_TYPES)[number]

export const MES_NODE_LABELS = [
  'Product',
  'Material',
  'Recipe',
  'Phase',
  'WorkOrder',
  'ProductionGoal',
  'Batch',
  'Operation',
  'ProcessDeviation',
  'FunctionalLocation',
  'Vendor',
  'QualityResult'
] as const
export type MESNodeLabel = (typeof MES_NODE_LABELS)[number]

export const BUSINESS_NODE_LABELS = [
  'Customer',
  'CustomerOrder',
  'DemandForecast',
  'DemandHistory'
] as const
export type BusinessNodeLabel = (typeof BUSINESS_NODE_LABELS)[number]

/**
 * Fault knowledge (FaultSymptom / FaultCause), the reusable Problem concept and legacy episode node
 * projected from stateful-trigger occurrences, and the Observer registry
 * projection. Problem and Observer require typed service writes
 * (`cypherWriteCreatable: false`).
 */
export const TROUBLESHOOTING_NODE_LABELS = [
  'FaultSymptom',
  'FaultCause',
  'Problem',
  'Observer',
  'Intent',
  'OperatorPhrase'
] as const
export type TroubleshootingNodeLabel = (typeof TROUBLESHOOTING_NODE_LABELS)[number]

/**
 * Job role vocabulary required by Procedures. Skill grades live on the
 * REQUIRES_SKILL_LEVEL edge so one Role node covers all grade levels.
 */
export const ROLE_NODE_LABELS = ['Role'] as const
export type RoleNodeLabel = (typeof ROLE_NODE_LABELS)[number]

/**
 * Manual labor contributions (HumanLabor) and Line-scoped shared staffing
 * capacity (CrewRequirement). Distinct from concrete steps (LineOperation),
 * authored instructions (Procedure), and named people/rosters (out of scope).
 * Canonical participation is LineOperation -[:REQUIRES_LABOR]-> HumanLabor;
 * Line→HumanLabor remains a compatibility projection. Shared capacity via
 * HAS_CREW_REQUIREMENT / COVERS.
 */
export const HUMAN_LABOR_NODE_LABELS = ['HumanLabor', 'CrewRequirement'] as const
export type HumanLaborNodeLabel = (typeof HUMAN_LABOR_NODE_LABELS)[number]

/**
 * Plant spatial regions (production zones, coolers, freezers, mezzanines,
 * machinery rooms, etc.). Hierarchical via PART_OF; equipment is placed via
 * LOCATED_IN.
 */
export const SPATIAL_NODE_LABELS = ['Area'] as const
export type SpatialNodeLabel = (typeof SPATIAL_NODE_LABELS)[number]

/**
 * Safety-lifecycle labels. A ProtectiveFunction is the logical SIF/IPL
 * identity; sensors and final elements remain Equipment; the logic solver
 * is a LogicSolver (not a PLC Controller).
 */
export const SAFETY_NODE_LABELS = [
  'ProtectiveFunction',
  'LogicSolver',
  'SrsSection',
  'ReferenceDocument',
  'HazardScenario',
  'FunctionLogicDiagram',
  'SafetyPoint'
] as const
export type SafetyNodeLabel = (typeof SAFETY_NODE_LABELS)[number]

/**
 * Network / PCN (Process Control Network) labels — the enterprise model from
 * the Experion EMDB (`epks_emdb`). `Asset` is the enterprise asset hierarchy
 * (ASSET template), `NetworkSegment` is a network segment / FTE community, and
 * `NetworkDevice` is a switch / FTE device / other network equipment.
 * Computers and servers are modeled as `Compute`, not a network label.
 * `EmdbProject` is an Experion System Management folder (distinct from
 * Ignition Perspective `Project`).
 */
export const NETWORK_NODE_LABELS = [
  'Asset',
  'NetworkSegment',
  'NetworkDevice',
  'EmdbProject'
] as const
export type NetworkNodeLabel = (typeof NETWORK_NODE_LABELS)[number]

/**
 * Vendor- or customer-authored instructions for equipment-specific work
 * (inspection, changeover, cleaning, LOTO, SOP). Steps live on the
 * `steps[]` JSON property.
 */
export const PROCEDURE_NODE_LABELS = ['Procedure'] as const
export type ProcedureNodeLabel = (typeof PROCEDURE_NODE_LABELS)[number]

/**
 * Utility-service vocabulary nodes (electrical classes, steam, refrigeration
 * loops, water types, compressed air, gas). Per-equipment specs ride on
 * REQUIRES_UTILITY edge properties.
 */
export const UTILITY_NODE_LABELS = ['Utility'] as const
export type UtilityNodeLabel = (typeof UTILITY_NODE_LABELS)[number]

/**
 * Hexagon PlantState Suite (PSS / PSI) mirror projections — the mirrored
 * tags and the per-data-owner event journals they log into, hung under the
 * managed `urn:db:<name>` `Database` (`source_kind: 'pss_mirror'`).
 *
 * A `PssTag` is deliberately **not** a SCADA `Tag`: it has no current value
 * and no sampled history — the only data behind it is the event journal.
 */
export const PSS_NODE_LABELS = ['PssTag', 'PssEventJournal'] as const
export type PssNodeLabel = (typeof PSS_NODE_LABELS)[number]

/**
 * Flat runtime list of every Neo4j label the ontology recognises.
 *
 * The cypher write validator iterates this to reject any `:LabelX` token
 * that is not part of the published ontology, so adding a new label to
 * one of the layer arrays above is the only change needed for it to be
 * accepted by the validator.
 */
export const NODE_LABELS = [
  ...PLC_NODE_LABELS,
  ...ROBOTICS_NODE_LABELS,
  ...VISION_NODE_LABELS,
  ...DCS_NODE_LABELS,
  ...ORG_NODE_LABELS,
  ...SOAP_NODE_LABELS,
  ...COMPUTE_NODE_LABELS,
  ...IGNITION_INTERNAL_NODE_LABELS,
  ...DATABASE_NODE_LABELS,
  ...ASSET_NODE_LABELS,
  ...PROCESS_NODE_LABELS,
  ...MES_NODE_LABELS,
  ...BUSINESS_NODE_LABELS,
  ...TROUBLESHOOTING_NODE_LABELS,
  ...ROLE_NODE_LABELS,
  ...HUMAN_LABOR_NODE_LABELS,
  ...SPATIAL_NODE_LABELS,
  ...SAFETY_NODE_LABELS,
  ...NETWORK_NODE_LABELS,
  ...PROCEDURE_NODE_LABELS,
  ...UTILITY_NODE_LABELS,
  ...PSS_NODE_LABELS
] as const

export type NodeLabel = (typeof NODE_LABELS)[number]

// -- Schema definitions consumed by Neo4j adapter's createIndexes() --

export interface UniqueConstraint {
  label: NodeLabel
  property: string
}

export interface SchemaIndex {
  label: NodeLabel | '*'
  property: string
}

export const UNIQUE_CONSTRAINTS: readonly UniqueConstraint[] = [
  // PLC
  { label: 'Controller', property: 'id' },
  { label: 'Program', property: 'id' },
  { label: 'Routine', property: 'id' },
  { label: 'AOI', property: 'id' },
  { label: 'PLCDataType', property: 'id' },
  { label: 'PLCDataTypeMember', property: 'id' },
  { label: 'PLCTag', property: 'id' },
  { label: 'ControlPattern', property: 'id' },
  { label: 'DataFlow', property: 'id' },
  // Robotics
  { label: 'RobotController', property: 'id' },
  { label: 'RobotArm', property: 'id' },
  { label: 'RobotJob', property: 'id' },
  { label: 'RobotIOSignal', property: 'id' },
  { label: 'RobotCondition', property: 'id' },
  { label: 'VisionProcess', property: 'id' },
  { label: 'VisionData', property: 'id' },
  // Vision
  { label: 'VisionSystem', property: 'id' },
  { label: 'VisionJob', property: 'id' },
  // DCS
  { label: 'DcsController', property: 'id' },
  { label: 'Cee', property: 'id' },
  { label: 'ControlModule', property: 'id' },
  { label: 'FunctionBlock', property: 'id' },
  { label: 'BlockType', property: 'id' },
  { label: 'BlockParamGroup', property: 'id' },
  { label: 'BlockLibrary', property: 'id' },
  { label: 'IoModule', property: 'id' },
  { label: 'IoChannel', property: 'id' },
  { label: 'ControlNamespace', property: 'id' },
  { label: 'ControlTag', property: 'id' },
  { label: 'ControlAlarm', property: 'id' },
  { label: 'ControlSetpoint', property: 'id' },
  // Org
  { label: 'Organization', property: 'id' },
  { label: 'Site', property: 'id' },
  // SOAP integration metadata
  { label: 'SoapEndpoint', property: 'id' },
  { label: 'SoapContract', property: 'id' },
  { label: 'SoapOperation', property: 'id' },
  // Compute
  { label: 'Compute', property: 'id' },
  { label: 'TagProvider', property: 'id' },
  { label: 'Tag', property: 'id' },
  { label: 'Folder', property: 'id' },
  { label: 'Database', property: 'id' },
  { label: 'Table', property: 'id' },
  { label: 'Alarm', property: 'id' },
  // Ignition-internal
  { label: 'Project', property: 'id' },
  { label: 'View', property: 'id' },
  { label: 'ViewComponent', property: 'id' },
  { label: 'UDTDefinition', property: 'id' },
  { label: 'UDTMember', property: 'id' },
  { label: 'UDTParameter', property: 'id' },
  { label: 'UDTInstance', property: 'id' },
  { label: 'Script', property: 'id' },
  { label: 'GatewayEvent', property: 'id' },
  { label: 'GatewayEventHandler', property: 'id' },
  { label: 'AuditProfile', property: 'id' },
  { label: 'ScheduleProfile', property: 'id' },
  { label: 'Holiday', property: 'id' },
  // Database
  { label: 'NamedQuery', property: 'id' },
  { label: 'QueryTag', property: 'id' },
  // Asset
  { label: 'EquipmentProperty', property: 'id' },
  { label: 'Observable', property: 'id' },
  { label: 'Setpoint', property: 'id' },
  { label: 'Part', property: 'id' },
  // Process
  { label: 'Line', property: 'id' },
  { label: 'Process', property: 'id' },
  { label: 'LineOperation', property: 'id' },
  { label: 'Equipment', property: 'id' },
  // MES
  { label: 'Product', property: 'id' },
  { label: 'Material', property: 'id' },
  { label: 'Recipe', property: 'id' },
  { label: 'Phase', property: 'id' },
  { label: 'WorkOrder', property: 'id' },
  { label: 'ProductionGoal', property: 'id' },
  { label: 'Batch', property: 'id' },
  { label: 'Operation', property: 'id' },
  { label: 'ProcessDeviation', property: 'id' },
  { label: 'FunctionalLocation', property: 'id' },
  { label: 'Vendor', property: 'id' },
  { label: 'QualityResult', property: 'id' },
  // Business
  { label: 'Customer', property: 'id' },
  { label: 'CustomerOrder', property: 'id' },
  { label: 'DemandForecast', property: 'id' },
  { label: 'DemandHistory', property: 'id' },
  // Troubleshooting
  { label: 'FaultSymptom', property: 'id' },
  { label: 'FaultCause', property: 'id' },
  { label: 'Problem', property: 'id' },
  { label: 'Observer', property: 'id' },
  { label: 'Intent', property: 'id' },
  { label: 'OperatorPhrase', property: 'id' },
  // Role / human-labor / spatial / procedure / utility
  { label: 'Role', property: 'id' },
  { label: 'HumanLabor', property: 'id' },
  { label: 'CrewRequirement', property: 'id' },
  { label: 'Area', property: 'id' },
  { label: 'ProtectiveFunction', property: 'id' },
  { label: 'LogicSolver', property: 'id' },
  { label: 'SrsSection', property: 'id' },
  { label: 'ReferenceDocument', property: 'id' },
  { label: 'HazardScenario', property: 'id' },
  { label: 'FunctionLogicDiagram', property: 'id' },
  { label: 'SafetyPoint', property: 'id' },
  // Network / PCN
  { label: 'Asset', property: 'id' },
  { label: 'NetworkSegment', property: 'id' },
  { label: 'NetworkDevice', property: 'id' },
  { label: 'EmdbProject', property: 'id' },
  { label: 'Procedure', property: 'id' },
  { label: 'Utility', property: 'id' },
  // PSS mirror projections
  { label: 'PssTag', property: 'id' },
  { label: 'PssEventJournal', property: 'id' }
] as const

export const INDEXES: readonly SchemaIndex[] = [
  { label: '*', property: 'name' },
  { label: 'PLCTag', property: 'aoi_name' },
  { label: 'Tag', property: 'tag_system' },
  { label: 'Tag', property: 'ignition_tag_type' },
  // `tag_path` is the canonical live-data path (`[Provider]Folder/Tag`,
  // `[MTC:host:port]device/...`, etc.) and is what every downstream live-data
  // lookup, condition trigger, etc.
  // joins / filters on. Index it so the plant browser's per-instance member
  // pulls and the proxy's `tag_path:` AIP filter stay O(log n) instead of
  // full-scanning the `:Tag` set on every ingest.
  { label: 'Tag', property: 'tag_path' },
  { label: 'Folder', property: 'path' },
  { label: 'Folder', property: 'provider_name' },
  { label: 'UDTMember', property: 'member_type' },
  { label: 'Equipment', property: 'equipment_type' },
  { label: 'ProtectiveFunction', property: 'function_type' },
  { label: 'SafetyPoint', property: 'point_type' },
  { label: 'SafetyPoint', property: 'tag_number' },
  { label: 'FunctionLogicDiagram', property: 'fld_number' },
  { label: 'EquipmentProperty', property: 'kind' },
  { label: 'FaultSymptom', property: 'symptom' },
  { label: 'FaultSymptom', property: 'kind' },
  // The Problems dashboard and every "what's open on X" traversal filter on
  // status; the projector resolves an occurrence's node by owner + trigger.
  { label: 'Problem', property: 'status' },
  { label: 'Problem', property: 'owner_id' },
  { label: 'OperatorPhrase', property: 'phrase' },
  { label: 'ProcessDeviation', property: 'batch_name' },
  { label: 'QualityResult', property: 'batch_name' },
  { label: 'Operation', property: 'production_order_name' },
  // SRS → PSS tag mapping and `get_pss_events` resolve a mirrored tag by its
  // verbatim PSS name; mirrors carry thousands of tags per data owner.
  { label: 'PssTag', property: 'name' }
] as const
